<template>
    <div id="app" :class="{ 'hide-vertical-scroll': hideVerticalScroll}">
        <Header />
        <transition name="fast-fade">
            <div v-show="showLoadingBlocker" id="spinner-wrapper">
                <div id="spinner-inner-wrapper">
                    <div id="ball-wrapper" v-show="showLoadingSpinner">
                        <div class="ball1" />
                        <div class="ball2" />
                        <div class="ball3" />
                    </div>
                    <br />
                    <div v-show="showLoadingMessage && loadingMessage" class="loading-message">
                        {{ loadingMessage }}
                    </div>
                </div>
            </div>
        </transition>
        <div id="app-wrapper">
            <transition name="zoom" mode="out-in">
                <router-view />
            </transition>
        </div>
        <div id="footer" v-if="hasCheck && !isErrorPage && !isLinksPage">
            <Balance />
        </div>
    </div>
</template>

<script>
    import axios from "axios";
    import Header from "./components/Header";
    import Balance from "./components/Balance";
    import { mapState, mapMutations } from "vuex";
    import { appSettings } from "./mixins/app-settings";

    export default {
        name: "App",
        mixins: [ appSettings ],
        components: {
            Header,
            Balance
        },
        computed: {
            ...mapState({
                appName: state => state.session.appName,
                appIcon: state => state.session.appIcon,
                showLoadingBlocker: state => state.session.showLoadingBlocker,
                showLoadingSpinner: state => state.session.showLoadingSpinner,
                showLoadingMessage: state => state.session.showLoadingMessage,
                loadingMessage: state => state.session.loadingMessage,
                googlePayScriptLoaded: state => state.session.googlePayScriptLoaded,
                apiBaseUrl: state => state.session.apiBaseUrl
            }),
            hasCheck() {
                return this.$store.state.session.check !== null;
            },
            isErrorPage() {
                return this.$route.name.toLowerCase() == "error";
            },
            isLinksPage() {
                return this.$route.name.toLowerCase() == "links";
            },
            hideVerticalScroll() {
                return this.showLoadingBlocker;
            }
        },
        watch: {
            appName: function (value) {
                document.title = value;
            },
            appIcon: function (value) {
                let link = document.querySelector("link[rel='icon']");
                link.href = value;
            }
        },
        methods: {
            ...mapMutations([
                "setCustomization",
                "setGooglePayScriptLoaded",
                "setApiBaseUrl"
            ])
        },
        beforeCreate() {
            if (!this.googlePayScriptLoaded) {
                this.$loadScript("https://pay.google.com/gp/p/js/pay.js")
                    .then(() => {
                        this.setGooglePayScriptLoaded(true);
                    })
                    .catch(() => {
                        console.error("Unable to load Google Pay script.");
                    })
            }           
        },
        async mounted() {
            axios.interceptors.response.use((response) => {
                    return response;
                }, error => {
                    // when jwt token expires, redirect to error page prompting user to scan code again.
                    if (error.response.status === 401) {
                        axios.defaults.headers.common['Authorization'] = null;
                        this.$router.push({ name: "Error", params: { errorCode: "1001" } });
                    }
                
                    return Promise.reject(error);
                });

            await axios.get(this.getApiBaseUrl() + "/app/customizationsettings")
                .then(response => {
                    const settings = response.data;

                    // Save to session so other views can display customized values.
                    this.setCustomization({
                        appName: settings.appName,
                        appIcon: settings.appIcon
                    });
                })
                .catch(error => {
                    console.error(error);
                });
        }
    }
</script>

<style>
    @import "./assets/styles/main.css";
    @import "./assets/styles/form.css";
    @import "./assets/styles/animations.css";

    #spinner-wrapper {
        position: absolute;
        width: 100%;
        height: 100%;
        z-index: 1000;
        display: flex;
        flex-direction: column;
        align-items: stretch;
    }

    #spinner-inner-wrapper {
        margin-top: 80px;
        flex-grow: 1;
        background-color: rgba(255, 255, 255, 0.95);
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
    }

    #ball-wrapper {
        margin-bottom: 40px;
    }

    .loading-message {
        position: absolute;
        max-width: 210px;
        margin: 80px 40px 0 40px;
        line-height: 20px;
    }

    #app > div:first-child {
        /* Header */
        height: 80px;
        position: fixed;
        left: 0;
        top: 0;
        width: 100%;
        z-index: 500;
    }

    #app-wrapper {
        height: 100%;
        display: flex;
        flex-direction: column;
        align-items: stretch;
        margin: 0 auto;
        max-width: var(--check-max-width);
    }

        #app-wrapper > div:first-child {
            /* Router view (main content) */
            flex-grow: 1;
            margin-top: 90px;
        }

    #footer {
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        z-index: 1;
    }

    .hide-vertical-scroll {
        overflow-y: hidden;
    }
</style>
